// src/admin/pages/AdminNotifications.js
import React, { useEffect, useState, useContext } from "react";
import AdminSidebar from "../AdminSidebar";
import AdminHeader from "../AdminHeader";
import {
    FaBell,
    FaExclamationTriangle,
    FaShoppingCart,
    FaBoxOpen,
    FaEnvelope,
    FaCheckCircle
} from "react-icons/fa";
import {
  getAdminNotifications,
  markNotificationRead,
  deleteNotification,
} from "../../services/adminNotifications.api";
import { AdminLanguageContext } from "../context/AdminLanguageContext";

function AdminNotifications() {
    const [isOpen, setIsOpen] = useState(false);
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("All");

    const { lang } = useContext(AdminLanguageContext);
    
    const unreadCounts = {
  order: notifications.filter(
    (n) => n.type === "order" && !n.read
  ).length,
  stock: notifications.filter(
    (n) => n.type === "stock" && !n.read
  ).length,
  message: notifications.filter(
    (n) => n.type === "message" && !n.read
  ).length,
};

    const TEXT = {
        EN: {
            title: "Notifications",
            markAll: "Mark All as Read",
            filters: {
                All: "All",
                order: "Orders",
                stock: "Stock",
                message: "Messages",
            },
            loading: "Loading notifications...",
            empty: "No notifications found.",
            markRead: "Mark Read",

            // notification titles/messages
            orderTitle: "New Order Received",
            orderMsg: "Order #1042 has been placed.",
            stockTitle: "Low Stock",
            stockMsg: "Product 'Mini Car Toy' has only 3 left.",
            messageTitle: "New Customer Message",
            messageMsg: "A new support ticket was submitted.",
        },
        AR: {
            title: "الإشعارات",
            markAll: "تحديد الكل كمقروء",
            filters: {
                All: "الكل",
                order: "الطلبات",
                stock: "المخزون",
                message: "الرسائل",
                system: "النظام",
            },
            loading: "جاري تحميل الإشعارات...",
            empty: "لا توجد إشعارات.",
            markRead: "تحديد كمقروء",

            orderTitle: "طلب جديد",
            orderMsg: "تم إنشاء الطلب رقم 1042.",
            stockTitle: "مخزون منخفض",
            stockMsg: "المنتج (Mini Car Toy) بقي منه 3 فقط.",
            messageTitle: "رسالة جديدة",
            messageMsg: "تم استلام تذكرة دعم جديدة.",
        },
    }[lang];

   useEffect(() => {
  setLoading(true);
  getAdminNotifications()
    .then((rows) => {
      const mapped = rows
  .filter((n) => n.type !== "other")
  .sort((a, b) => new Date(b.created_at) - new Date(a.created_at)) // ✅ NEWEST FIRST
  .map((n) => ({
    id: n.id,
    type:
      n.type === "new_order"
        ? "order"
        : n.type === "new_message"
        ? "message"
        : n.type === "low_stock"
        ? "stock"
        : "other",
    title: n.title,
    message: n.body,
    date: new Date(n.created_at).toLocaleString(),
    read: n.is_read,
    icon:
      n.type === "new_order" ? (
        <FaShoppingCart className="text-cyan-600" />
      ) : n.type === "new_message" ? (
        <FaEnvelope className="text-green-600" />
      ) : (
        <FaBoxOpen className="text-yellow-600" />
      ),
  }));


      setNotifications(mapped);
    })
    .finally(() => setLoading(false));
}, [lang]);


    const filtered = notifications.filter((n) =>
        filter === "All" ? true : n.type === filter
    );

    const markRead = async (id) => {
  await markNotificationRead(id);
  setNotifications((prev) =>
    prev.map((n) => (n.id === id ? { ...n, read: true } : n))
  );
};


    const markAll = async () => {
  const unreadIds = notifications.filter((n) => !n.read).map((n) => n.id);

  await Promise.all(unreadIds.map((id) => markNotificationRead(id)));

  setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
};


    return (
        <div className={`flex bg-gray-100 min-h-screen ${isOpen ? "overflow-hidden" : ""}`}>
            <AdminSidebar isOpen={isOpen} setIsOpen={setIsOpen} />

            <div className="flex-1 lg:ml-64 transition-all duration-300">
                <AdminHeader setIsOpen={setIsOpen} />

                <main className="p-4 sm:p-6 animate-fadeSlide">

                    {/* HEADER */}
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                        <h1 className="text-2xl font-bold text-cyan-700 flex items-center gap-2">
                            <FaBell /> {TEXT.title}
                        </h1>

                        <button
                            onClick={markAll}
                            className="bg-cyan-600 text-white px-4 py-2 rounded-xl hover:bg-cyan-700 w-full sm:w-auto"
                        >
                            {TEXT.markAll}
                        </button>
                    </div>

                    {/* FILTERS */}
                    <div className="flex flex-wrap gap-3 mb-5">
  {["All", "order", "stock", "message"].map((f) => {
    const count = unreadCounts[f];

    return (
      <div key={f} className="relative">
        <button
          onClick={() => setFilter(f)}
          className={`px-4 py-2 rounded-xl border capitalize ${
            filter === f
              ? "bg-cyan-600 text-white"
              : "bg-white hover:bg-gray-100"
          }`}
        >
          {TEXT.filters[f]}
        </button>

        {/* 🔔 Unread badge */}
        {count > 0 && (
          <span
            className="
              absolute -top-2 -right-2
              min-w-[20px] h-[20px]
              px-1
              rounded-full
              bg-red-600 text-white
              text-[11px]
              flex items-center justify-center
              font-semibold
            "
          >
            {count}
          </span>
        )}
      </div>
    );
  })}
</div>


                    {/* LIST */}
                    <div className="bg-white shadow-lg rounded-xl p-4">
                        {loading ? (
                            <p className="text-center py-10">{TEXT.loading}</p>
                        ) : filtered.length === 0 ? (
                            <p className="text-center py-10 text-gray-600">{TEXT.empty}</p>
                        ) : (
                            filtered.map((n) => (
                                <div
                                    key={n.id}
                                    className={`p-4 border-b flex flex-col sm:flex-row gap-4 items-start sm:items-center hover:bg-gray-50 ${
                                        !n.read ? "bg-blue-50" : ""
                                    }`}
                                >
                                    <div className="text-2xl">{n.icon}</div>

                                    <div className="flex-1">
                                        <h3 className="font-semibold text-gray-800">{n.title}</h3>
                                        <p className="text-gray-600">{n.message}</p>
                                        <span className="text-xs text-gray-500">{n.date}</span>
                                    </div>
                                     <button
 onClick={async () => {
  await deleteNotification(n.id);
  setNotifications((prev) => prev.filter((x) => x.id !== n.id));
}}
  className="text-red-500 text-sm hover:underline"
>
  Delete
</button>

                                    {!n.read && (
                                        <button
                                            onClick={() => markRead(n.id)}
                                            className="text-cyan-600 text-sm hover:underline flex items-center gap-1"
                                        >
                                            <FaCheckCircle /> {TEXT.markRead}
                                        </button>
                                    )}
                                </div>
                            ))
                        )}
                    </div>

                </main>
            </div>
        </div>
    );
}

export default AdminNotifications;
